import { DateTime } from "luxon";
import { type TranslatedString, untranslated } from "@Helpers/types/i18n";

export interface PortalDashboardDate {
  date: string;
  day: string;
  time: string;
  timeZone: string;
}

function convertDate<F extends string>(
  // `unknown`, not a date union: ~400 call sites feed this straight from server payloads, and the
  // only thing done with it is the `!date` / `Number(date)` guard pair below, which accepts anything.
  date: unknown,
  dateFormat: F,
  // IANA zone name, forwarded to luxon `setZone` by the dateForSchedule formatter only.
  timeZone?: string,
): F extends "dateForPortalDashboard" ? PortalDashboardDate : TranslatedString {
  type Result = F extends "dateForPortalDashboard" ? PortalDashboardDate : TranslatedString;

  if (!date || isNaN(Number(date))) {
    return untranslated("") as Result;
  }
  const time = DateTime.fromMillis(Number(date));

  // Special case for portal dashboard - returns object
  if (dateFormat === "dateForPortalDashboard") {
    return {
      date: time.toFormat("dd MMM yy"),
      day: time.toFormat("cccc"),
      time: time.toFormat("hh:mm:ss a"),
      timeZone: time.toFormat("ZZ"),
    } as Result;
  }

  const formatMap: Record<string, () => string | null> = {
    dateForText: () => `${time.toFormat("MMM dd")} at ${time.toFormat("hh:mm a")}`,
    dateForTooltip: () => `${time.toFormat("MMM dd,yyyy")} at ${time.toFormat("hh:mm:ss a")}`,
    dateForProfile: () => time.toFormat("MMM dd, yyyy"),
    dateForOrganization: () => time.toFormat("MMM dd, yyyy"),
    NormalDate: () => time.toFormat("dd/MM/yyyy"),
    NormalDateTime: () => time.toFormat("MM/dd/yyyy hh:mm a"),
    getDifferenceTime: () => time.toRelative(),
    dateForToolTipModal: () => time.toFormat("MMM dd, yyyy"),
    dateForTooltipSpaceBeforeYear: () => `${time.toFormat("MMM dd, yyyy")} at ${time.toFormat("hh:mm:ss a")}`,
    dateForSignHistory: () => time.toFormat("MMM dd hh:mm a"),
    standardIsoFormat: () => time.toISO(),
    dateForPdfExport: () => `${time.toFormat("MMM dd, yyyy")} at ${time.toFormat("hh:mm a")}`,
    dateForSchedule: () => {
      const timeWithTimezone = timeZone ? time.setZone(timeZone) : time;
      return `${timeWithTimezone.toFormat("MMM dd, yyyy")} at ${timeWithTimezone.toFormat("hh:mm a")}`;
    },
    dateforProjectDashbaord: () => `${time.toFormat("dd MMM, yy")} ${time.toFormat("hh:mm a")}`,
    chatBot: () => `At ${time.toFormat("hh:mm a")}`,
  };

  const defaultFormat = () => time.toFormat(dateFormat);
  const formatter = formatMap[dateFormat] || defaultFormat;

  return untranslated(formatter() ?? "") as Result;
}

export { convertDate };
